import { useQuery } from "@tanstack/react-query";

import { PREVIEW_COACH_USER_ID } from "@/data/leaderPreviewData";
import { useAuth } from "@/hooks/useAuth";
import { buildCoachHealthEvidenceProfile } from "@/lib/coachHealth/buildCoachHealthEvidenceProfile";
import {
  COACH_HEALTH_EVIDENCE_INPUT_VERSION,
  type CoachHealthEvidenceProfile,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";
import { COACH_HEALTH_EVIDENCE_SOURCE_TYPES } from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";
import {
  buildCoachHealthScopeKey,
  coachHealthProfileQueryKey,
} from "@/lib/coachHealth/coachHealthScope";
import {
  fetchCoachHealthEvidenceInput,
  type CoachHealthEvidenceInputSnapshot,
} from "@/lib/coachHealth/fetchCoachHealthEvidenceInput";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

const SOURCE_READINESS = COACH_HEALTH_EVIDENCE_SOURCE_TYPES.map((sourceType) => ({
  sourceType,
  state: "ready" as const,
}));

interface CoachHealthProfileQueryData {
  readonly scopeKey: string;
  readonly snapshot: CoachHealthEvidenceInputSnapshot;
  readonly profile: CoachHealthEvidenceProfile;
}

function fallbackProfile(params: {
  readonly coachId: string | null;
  readonly state: "idle" | "loading" | "error";
}): CoachHealthEvidenceProfile {
  if (params.coachId === null) {
    return {
      version: "coach_health_evidence_profile_v2",
      state: "idle",
      asOf: null,
      errorCode: null,
      eligibleLeaderIds: [],
      eligibleRelationshipKeys: [],
      exclusionCounts: [],
      sourceIssues: [],
      dimensions: null,
      recordedContextCoverage: null,
      leaderPerspectiveState: null,
    };
  }
  const asOf = new Date().toISOString();
  const scopeKey = params.coachId
    ? buildCoachHealthScopeKey(params.coachId)
    : "coach:inactive";
  return buildCoachHealthEvidenceProfile({
    version: COACH_HEALTH_EVIDENCE_INPUT_VERSION,
    enabled: true,
    authenticatedCoachId: params.coachId,
    currentScopeKey: scopeKey,
    snapshotScopeKey: scopeKey,
    asOf,
    portfolioState:
      params.state === "idle"
        ? "ready"
        : params.state === "loading"
          ? "loading"
          : "error",
    relationships: [],
    sourceReadiness: SOURCE_READINESS,
    evidence: [],
  });
}

export function useCoachHealthEvidenceProfile() {
  const { user, roles, loading } = useAuth();
  const coachId =
    user &&
    !loading &&
    roles.includes("coach") &&
    user.id !== PREVIEW_COACH_USER_ID &&
    user.id !== PREVIEW_LEADER_USER_ID
      ? user.id
      : null;
  const scopeKey = coachId ? buildCoachHealthScopeKey(coachId) : null;

  const query = useQuery({
    queryKey: coachId
      ? coachHealthProfileQueryKey(coachId)
      : ["coach-health-evidence-profile-v2", "inactive"] as const,
    enabled: coachId !== null,
    queryFn: async (): Promise<CoachHealthProfileQueryData> => {
      if (!coachId || !scopeKey) throw new Error("Inactive Coaching Health scope");
      const snapshot = await fetchCoachHealthEvidenceInput({
        authenticatedCoachId: coachId,
        asOf: new Date().toISOString(),
      });
      return {
        scopeKey,
        snapshot,
        profile: buildCoachHealthEvidenceProfile(snapshot.input),
      };
    },
    placeholderData: (previous) =>
      previous?.scopeKey === scopeKey ? previous : undefined,
    staleTime: 30_000,
  });

  const currentData =
    !query.isError && query.data?.scopeKey === scopeKey
      ? query.data
      : undefined;
  const profile = currentData?.profile ?? fallbackProfile({
    coachId,
    state: coachId === null
      ? "idle"
      : query.isError
        ? "error"
        : "loading",
  });

  return {
    profile,
    leaderLabelsById: currentData?.snapshot.leaderLabelsById ?? {},
    timeZone: currentData?.snapshot.timeZone ?? "UTC",
    freshness: currentData && query.isFetching
      ? "refreshing" as const
      : "fresh" as const,
    isFetching: coachId !== null && query.isFetching,
    retry: query.refetch,
  };
}
